import React, { useState, useEffect } from 'react';
import {
  FileField,
  FormField,
  InlineAlert,
  Modal,
  SelectField,
  TextAreaField,
} from './ui';
import api from '../../services/api';

const StudentFormDialog = ({ open, onClose, student, onSuccess }) => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    biography: '',
    current_belt_id: '',
  });
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [belts, setBelts] = useState([]);
  const [loadingBelts, setLoadingBelts] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setError('');
    const load = async () => {
      setLoadingBelts(true);
      try {
        const response = await api.get('/public/belts');
        setBelts(response.data.data);
      } catch (err) {
        console.error('Error fetching belts:', err);
      } finally {
        setLoadingBelts(false);
      }
    };
    load();

    if (student) {
      setFormData({
        first_name: student.first_name || '',
        last_name: student.last_name || '',
        phone: student.phone || '',
        biography: student.biography || '',
        current_belt_id: student.current_belt_id || '',
      });
      setPhotoFile(null);
      if (student.photo_path) {
        const base = (process.env.REACT_APP_API_URL || '').replace('/api', '');
        setPhotoPreview(`${base}/uploads/${student.photo_path}`);
      } else {
        setPhotoPreview(null);
      }
    } else {
      setFormData({
        first_name: '',
        last_name: '',
        phone: '',
        biography: '',
        current_belt_id: '',
      });
      setPhotoFile(null);
      setPhotoPreview(null);
    }
  }, [open, student]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setPhotoPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('first_name', formData.first_name);
      formDataToSend.append('last_name', formData.last_name);
      if (formData.phone) formDataToSend.append('phone', formData.phone);
      if (formData.biography) formDataToSend.append('biography', formData.biography);
      if (formData.current_belt_id) {
        formDataToSend.append('current_belt_id', formData.current_belt_id);
      }
      if (photoFile) formDataToSend.append('photo', photoFile);

      if (student) {
        await api.put(`/admin/students/${student.id}`, formDataToSend, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else {
        await api.post('/admin/students', formDataToSend, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
      onSuccess();
      onClose();
    } catch (err) {
      console.error('Error saving student:', err);
      setError(err.response?.data?.message || 'Error al guardar el alumno');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      title={student ? 'Editar alumno' : 'Agregar alumno'}
      onClose={submitting ? undefined : onClose}
      footer={
        <>
          <button type="button" className="btn-admin-secondary" onClick={onClose} disabled={submitting}>
            Cancelar
          </button>
          <button
            type="submit"
            form="student-form"
            className="btn-admin"
            disabled={submitting || loadingBelts}
          >
            {submitting ? 'Guardando…' : student ? 'Guardar' : 'Crear'}
          </button>
        </>
      }
    >
      <form id="student-form" className="space-y-4" onSubmit={handleSubmit}>
        {error && <InlineAlert tone="error">{error}</InlineAlert>}
        <div className="flex flex-col items-center gap-3">
          {photoPreview ? (
            <img src={photoPreview} alt="" className="h-24 w-24 rounded-full object-cover" />
          ) : (
            <div className="flex h-24 w-24 items-center justify-center rounded-full bg-primary text-xl font-bold text-white">
              {(formData.first_name?.[0] || '') + (formData.last_name?.[0] || '') || '?'}
            </div>
          )}
          <FileField id="photo" label="Foto" accept="image/*" onChange={handlePhotoChange} />
        </div>
        <FormField id="first_name" label="Nombre" value={formData.first_name} onChange={handleChange} required />
        <FormField id="last_name" label="Apellido" value={formData.last_name} onChange={handleChange} required />
        <FormField id="phone" label="Teléfono" value={formData.phone} onChange={handleChange} />
        <SelectField
          id="current_belt_id"
          label="Cinturón actual"
          value={String(formData.current_belt_id || '')}
          onChange={handleChange}
          placeholder="Sin cinturón"
          options={belts.map((b) => ({ value: String(b.id), label: b.name }))}
        />
        <TextAreaField id="biography" label="Biografía" value={formData.biography} onChange={handleChange} />
      </form>
    </Modal>
  );
};

export default StudentFormDialog;
